import { Badge, FlexBox, Page } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { MouseEvent, useCallback, useRef, useState } from 'react';

import { conversationsApi } from 'apis/conversations';
import { LegacyTeamPage } from 'components/teamPageLegacy/containers/teamPage';
import { SearchControls } from 'components/teamPageNew/components/searchControls';
import { useTeamTableData } from 'components/teamPageNew/hooks/useTeamTableData';
import { useTeamTableFilters } from 'components/teamPageNew/hooks/useTeamTableFilters';
import { twilioActions } from 'middleware/twilio/actions/twilioActions';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import { TeamPage } from './components/teamPage';

const NewTeamPage = (): JSX.Element => {
  const { legacyPresenceSidebar } = useFeatureFlag(['legacy-presence-sidebar']);
  const dispatch = useAppDispatch();
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const isMicrophoneBlocked = useAppSelector(
    (state) => state.telephony.isMicrophoneBlocked,
  );

  const twilioMakeInternalCall = useCallback(
    (userId: string) =>
      bindActionToPromise(
        dispatch,
        twilioActions.twilioMakeInternalCall,
      )(userId),
    [dispatch],
  );

  const hasConversationReplyAccess = useHasAccess('conversation.reply');

  const { page, activeFilters, setPage, setActiveFilters, clearFilters } =
    useTeamTableFilters('/team');

  const {
    ownId,
    favorites,
    membersWithPresence,
    memberCount,
    pageCount,
    loading,
    isManualRefresh,
    handleRefreshMembers,
    toggleFavorite,
    showUserModal,
    hasOrganizationMemberAccess,
    hasPresenceAccess,
    awayStatuses,
  } = useTeamTableData({ page, activeFilters });

  // Expansion state
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const { conversations, loading: conversationsLoading } =
    conversationsApi.useReadConversationsQuery(
      {
        agentIds: expandedMemberId ? [expandedMemberId] : [],
        status: ['open'],
      },
      {
        skip: !expandedMemberId,
        selectFromResult: ({ data, isLoading, isFetching }) => ({
          conversations: data?.data.conversations,
          loading: isLoading || isFetching,
        }),
      },
    );

  const toggleRowExpanded = useCallback(
    (memberId: string) => (e: MouseEvent<HTMLElement>) => {
      const clickedOnDropdownButton = !!(e.target as HTMLElement).closest(
        '[data-dropdown-button="true"]',
      );
      if (clickedOnDropdownButton) {
        return;
      }
      const open = expandedMemberId !== memberId;

      open ? ampli.teamAgentExpanded() : ampli.teamAgentCollapsed();
      open ? setExpandedMemberId(memberId) : setExpandedMemberId(null);
    },
    [expandedMemberId],
  );

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200">
            Team
            {memberCount != null && (
              <Badge size="medium" variant="secondary">
                {memberCount}
              </Badge>
            )}
          </FlexBox>
        }
        controls={
          <SearchControls
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            onRefreshMembers={handleRefreshMembers}
            isRefreshing={isManualRefresh}
          />
        }
      />
      <Page.Body>
        <TeamPage
          members={membersWithPresence}
          memberCount={memberCount}
          pageCount={pageCount}
          loading={loading}
          page={page}
          setPage={setPage}
          activeFilters={activeFilters}
          clearFilters={clearFilters}
          favorites={favorites}
          ownId={ownId}
          toggleFavorite={toggleFavorite}
          showFavorites={!legacyPresenceSidebar}
          showUserModal={showUserModal}
          awayStatuses={awayStatuses}
          hasPresenceAccess={hasPresenceAccess}
          hasOrganizationMemberAccess={hasOrganizationMemberAccess}
          tableWrapperRef={tableWrapperRef}
          expandedMemberId={expandedMemberId}
          onToggleExpand={toggleRowExpanded}
          conversations={conversations ?? []}
          conversationsLoading={conversationsLoading}
          twilioMakeInternalCall={twilioMakeInternalCall}
          isMicrophoneBlocked={isMicrophoneBlocked}
          hasConversationReplyAccess={hasConversationReplyAccess}
        />
      </Page.Body>
    </Page>
  );
};

export const TeamView = (): JSX.Element => {
  const { legacyTeamOverview } = useFeatureFlag(['legacy-team-overview']);
  return legacyTeamOverview ? <LegacyTeamPage /> : <NewTeamPage />;
};
